import { useState, useEffect } from "react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { SortablePageSection } from "@/components/SortablePageSection";

type SectionKey = 'goals' | 'marketplace' | 'events' | 'games' | 'meet_team' | 'contact' | 'about' | 'get_involved' | 'newsletter';

interface PageSectionsOrderManagerProps {
  goalsSectionOrder: number;
  marketplaceSectionOrder: number;
  eventsSectionOrder: number;
  gamesSectionOrder: number;
  meetTeamSectionOrder: number;
  contactSectionOrder: number;
  aboutSectionOrder: number;
  getInvolvedSectionOrder: number;
  newsletterSectionOrder: number;
  showCurrentGoals: boolean;
  showMarketplaceSection: boolean;
  showEventsSection: boolean;
  showGamesSection: boolean;
  showMeetTeamSection: boolean;
  showContactDetails: boolean;
  showAboutSection: boolean;
  showGetInvolvedSection: boolean;
  showNewsletterSection: boolean;
  goalsSectionBg: string;
  marketplaceSectionBg: string;
  eventsSectionBg: string;
  gamesSectionBg: string;
  meetTeamSectionBg: string;
  contactSectionBg: string;
  aboutSectionBg: string;
  getInvolvedSectionBg: string;
  newsletterSectionBg: string;
  onOrderChange: (orders: Record<SectionKey, number>) => void;
  onToggleGoals: (checked: boolean) => void;
  onToggleMarketplace: (checked: boolean) => void;
  onToggleEvents: (checked: boolean) => void;
  onToggleGames: (checked: boolean) => void;
  onToggleMeetTeam: (checked: boolean) => void;
  onToggleContact: (checked: boolean) => void;
  onToggleAbout: (checked: boolean) => void;
  onToggleGetInvolved: (checked: boolean) => void;
  onToggleNewsletter: (checked: boolean) => void;
  onBgChange: (key: string, value: string) => void;
}

interface SectionItem {
  id: string;
  label: string;
  description: string;
  key: SectionKey;
}

export function PageSectionsOrderManager({
  goalsSectionOrder,
  marketplaceSectionOrder,
  eventsSectionOrder,
  gamesSectionOrder,
  meetTeamSectionOrder,
  contactSectionOrder,
  aboutSectionOrder,
  getInvolvedSectionOrder,
  newsletterSectionOrder,
  showCurrentGoals,
  showMarketplaceSection,
  showEventsSection,
  showGamesSection,
  showMeetTeamSection,
  showContactDetails,
  showAboutSection,
  showGetInvolvedSection,
  showNewsletterSection,
  goalsSectionBg,
  marketplaceSectionBg,
  eventsSectionBg,
  gamesSectionBg,
  meetTeamSectionBg,
  contactSectionBg,
  aboutSectionBg,
  getInvolvedSectionBg,
  newsletterSectionBg,
  onOrderChange,
  onToggleGoals,
  onToggleMarketplace,
  onToggleEvents,
  onToggleGames,
  onToggleMeetTeam,
  onToggleContact,
  onToggleAbout,
  onToggleGetInvolved,
  onToggleNewsletter,
  onBgChange,
}: PageSectionsOrderManagerProps) {
  const [sections, setSections] = useState<SectionItem[]>([]);

  useEffect(() => {
    const items: SectionItem[] = [
      { id: 'goals', label: 'Fundraising Goals', description: 'Display fundraising goals on public page', key: 'goals' },
      { id: 'marketplace', label: 'Community Marketplace', description: 'Display marketplace items on fundraising page', key: 'marketplace' },
      { id: 'events', label: 'Upcoming Events', description: 'Display upcoming events on fundraising page', key: 'events' },
      { id: 'games', label: 'Games', description: 'Display active games on fundraising page', key: 'games' },
      { id: 'meet_team', label: 'Meet the Team', description: 'Display team member profiles on fundraising page', key: 'meet_team' },
      { id: 'contact', label: 'Contact Details', description: 'Display PTA contact information and form', key: 'contact' },
      { id: 'about', label: 'About Our PTA', description: 'Display about section on public page', key: 'about' },
      { id: 'get_involved', label: 'Get Involved', description: 'Display call-to-action to join the PTA', key: 'get_involved' },
      { id: 'newsletter', label: 'Newsletter', description: 'Display newsletter subscribe call-to-action', key: 'newsletter' },
    ];

    const orderMap: Record<string, number> = {
      goals: goalsSectionOrder,
      marketplace: marketplaceSectionOrder,
      events: eventsSectionOrder,
      games: gamesSectionOrder,
      meet_team: meetTeamSectionOrder,
      contact: contactSectionOrder,
      about: aboutSectionOrder,
      get_involved: getInvolvedSectionOrder,
      newsletter: newsletterSectionOrder,
    };

    items.sort((a, b) => (orderMap[a.key] ?? 0) - (orderMap[b.key] ?? 0));
    setSections(items);
  }, [goalsSectionOrder, marketplaceSectionOrder, eventsSectionOrder, gamesSectionOrder, meetTeamSectionOrder, contactSectionOrder, aboutSectionOrder, getInvolvedSectionOrder, newsletterSectionOrder]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);
    const newSections = arrayMove(sections, oldIndex, newIndex);
    setSections(newSections);

    const orderMap = {} as Record<SectionKey, number>;
    newSections.forEach((s, i) => {
      orderMap[s.key] = i + 1;
    });
    onOrderChange(orderMap);
  };

  const getChecked = (key: string) => {
    if (key === 'goals') return showCurrentGoals;
    if (key === 'marketplace') return showMarketplaceSection;
    if (key === 'events') return showEventsSection;
    if (key === 'games') return showGamesSection;
    if (key === 'meet_team') return showMeetTeamSection;
    if (key === 'contact') return showContactDetails;
    if (key === 'about') return showAboutSection;
    if (key === 'get_involved') return showGetInvolvedSection;
    if (key === 'newsletter') return showNewsletterSection;
    return true;
  };

  const getToggle = (key: string) => {
    if (key === 'goals') return onToggleGoals;
    if (key === 'marketplace') return onToggleMarketplace;
    if (key === 'events') return onToggleEvents;
    if (key === 'games') return onToggleGames;
    if (key === 'meet_team') return onToggleMeetTeam;
    if (key === 'contact') return onToggleContact;
    if (key === 'about') return onToggleAbout;
    if (key === 'get_involved') return onToggleGetInvolved;
    if (key === 'newsletter') return onToggleNewsletter;
    return () => {};
  };

  const getBg = (key: string) => {
    if (key === 'goals') return goalsSectionBg;
    if (key === 'marketplace') return marketplaceSectionBg;
    if (key === 'events') return eventsSectionBg;
    if (key === 'games') return gamesSectionBg;
    if (key === 'meet_team') return meetTeamSectionBg;
    if (key === 'contact') return contactSectionBg;
    if (key === 'about') return aboutSectionBg;
    if (key === 'get_involved') return getInvolvedSectionBg;
    if (key === 'newsletter') return newsletterSectionBg;
    return 'white';
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {sections.map((section) => (
            <SortablePageSection
              key={section.id}
              id={section.id}
              label={section.label}
              description={section.description}
              checked={getChecked(section.key)}
              onCheckedChange={getToggle(section.key)}
              sectionBg={getBg(section.key)}
              onBgChange={(value) => onBgChange(section.key, value)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
